import React, { useState, useEffect } from 'react';
import { 
  Bell, Mail, MessageSquare, Plus, Trash2, Loader2, 
  CheckCircle, XCircle, Users, Building2, Phone, Tag 
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const RECIPIENT_OPTIONS = [
  { id: 'owner', label: 'Business Owner', icon: Building2, description: 'Main account owner' },
  { id: 'admins', label: 'All Admins', icon: Users, description: 'Team members with admin role' },
  { id: 'dispatchers', label: 'All Dispatchers', icon: Users, description: 'Team members with dispatcher role' },
  { id: 'business_phone', label: 'Business Phone', icon: Phone, description: 'Main business phone number' },
  { id: 'custom', label: 'Custom Numbers/Emails', icon: Plus, description: 'Add specific recipients' },
];

export default function JobNotificationSettings() {
  const [settings, setSettings] = useState({
    job_accepted_sms_enabled: true,
    job_accepted_sms_recipients: ['owner'],
    job_accepted_sms_custom_numbers: [],
    job_rejected_sms_enabled: true,
    job_rejected_sms_recipients: ['owner'],
    job_rejected_sms_custom_numbers: [],
    job_accepted_email_enabled: true,
    job_accepted_email_recipients: ['owner'],
    job_accepted_email_custom_addresses: [],
    job_rejected_email_enabled: true,
    job_rejected_email_recipients: ['owner'],
    job_rejected_email_custom_addresses: [],
    job_status_changed_sms_enabled: true,
    job_status_changed_sms_recipients: ['owner'],
    job_status_changed_sms_custom_numbers: [],
    job_status_changed_email_enabled: true,
    job_status_changed_email_recipients: ['owner'],
    job_status_changed_email_custom_addresses: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newCustomNumber, setNewCustomNumber] = useState({ accepted: '', rejected: '', status_changed: '' });
  const [newCustomEmail, setNewCustomEmail] = useState({ accepted: '', rejected: '', status_changed: '' });
  const [resolvedRecipients, setResolvedRecipients] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchResolvedRecipients();
  }, []);

  const fetchResolvedRecipients = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/settings/job-notifications/resolved-recipients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResolvedRecipients(res.data);
    } catch {
      setResolvedRecipients(null);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/settings/job-notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      console.error('Failed to fetch job notification settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings) => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/settings/job-notifications`, newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(newSettings);
      toast.success('Job notification settings saved');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (field) => {
    const newSettings = { ...settings, [field]: !settings[field] };
    saveSettings(newSettings);
  };

  const handleRecipientToggle = (type, action, recipientId) => {
    const fieldName = `job_${action}_${type}_recipients`;
    const currentRecipients = settings[fieldName] || [];
    
    let newRecipients;
    if (currentRecipients.includes(recipientId)) {
      newRecipients = currentRecipients.filter(r => r !== recipientId);
    } else {
      newRecipients = [...currentRecipients, recipientId];
    }
    
    const newSettings = { ...settings, [fieldName]: newRecipients };
    saveSettings(newSettings);
  };

  const addCustomNumber = (action) => {
    const number = newCustomNumber[action].trim();
    if (!number) return;
    
    const fieldName = `job_${action}_sms_custom_numbers`;
    const currentNumbers = settings[fieldName] || [];
    
    if (currentNumbers.includes(number)) {
      toast.error('Number already added');
      return;
    }
    
    const newSettings = { ...settings, [fieldName]: [...currentNumbers, number] };
    saveSettings(newSettings);
    setNewCustomNumber({ ...newCustomNumber, [action]: '' });
  };

  const removeCustomNumber = (action, number) => {
    const fieldName = `job_${action}_sms_custom_numbers`;
    const currentNumbers = settings[fieldName] || [];
    const newSettings = { ...settings, [fieldName]: currentNumbers.filter(n => n !== number) };
    saveSettings(newSettings);
  };

  const addCustomEmail = (action) => {
    const email = newCustomEmail[action].trim();
    if (!email) return;
    
    // Basic email validation
    if (!email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    
    const fieldName = `job_${action}_email_custom_addresses`;
    const currentEmails = settings[fieldName] || [];
    
    if (currentEmails.includes(email)) {
      toast.error('Email already added');
      return;
    }
    
    const newSettings = { ...settings, [fieldName]: [...currentEmails, email] };
    saveSettings(newSettings);
    setNewCustomEmail({ ...newCustomEmail, [action]: '' });
  };

  const removeCustomEmail = (action, email) => {
    const fieldName = `job_${action}_email_custom_addresses`;
    const currentEmails = settings[fieldName] || [];
    const newSettings = { ...settings, [fieldName]: currentEmails.filter(e => e !== email) };
    saveSettings(newSettings);
  };

  if (loading) {
    return (
      <Card data-testid="job-notification-settings-card">
        <CardContent className="py-10 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const renderNotificationSection = (action, icon, title, color) => {
    const Icon = icon;
    const smsEnabled = settings[`job_${action}_sms_enabled`];
    const emailEnabled = settings[`job_${action}_email_enabled`];
    const smsRecipients = settings[`job_${action}_sms_recipients`] || [];
    const emailRecipients = settings[`job_${action}_email_recipients`] || [];
    const customNumbers = settings[`job_${action}_sms_custom_numbers`] || [];
    const customEmails = settings[`job_${action}_email_custom_addresses`] || [];

    return (
      <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${color}`} />
          <h4 className="font-medium">{title}</h4>
        </div>

        {/* SMS Notifications */}
        <div className="space-y-3 pl-2 border-l-2 border-blue-500/30 ml-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-blue-400" />
              <Label className="text-sm font-medium">SMS Notifications</Label>
            </div>
            <Switch
              checked={smsEnabled}
              onCheckedChange={() => handleToggle(`job_${action}_sms_enabled`)}
              disabled={saving}
              data-testid={`sms-${action}-toggle`}
            />
          </div>

          {smsEnabled && (
            <div className="space-y-3 pl-4">
              <p className="text-xs text-muted-foreground">Who should receive SMS alerts?</p>
              <div className="grid gap-2">
                {RECIPIENT_OPTIONS.filter(opt => opt.id !== 'custom').map(option => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`sms-${action}-${option.id}`}
                      checked={smsRecipients.includes(option.id)}
                      onCheckedChange={() => handleRecipientToggle('sms', action, option.id)}
                      disabled={saving}
                      data-testid={`sms-${action}-${option.id}`}
                    />
                    <label
                      htmlFor={`sms-${action}-${option.id}`}
                      className="text-sm cursor-pointer flex items-center gap-2"
                    >
                      <option.icon className="h-3 w-3 text-muted-foreground" />
                      {option.label}
                    </label>
                  </div>
                ))}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`sms-${action}-custom`}
                    checked={smsRecipients.includes('custom')}
                    onCheckedChange={() => handleRecipientToggle('sms', action, 'custom')}
                    disabled={saving}
                    data-testid={`sms-${action}-custom`}
                  />
                  <label htmlFor={`sms-${action}-custom`} className="text-sm cursor-pointer">
                    Custom phone numbers
                  </label>
                </div>
              </div>

              {smsRecipients.includes('custom') && (
                <div className="space-y-2 pt-2">
                  <div className="flex gap-2">
                    <Input
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={newCustomNumber[action]}
                      onChange={(e) => setNewCustomNumber({ ...newCustomNumber, [action]: e.target.value })}
                      className="flex-1 h-8 text-sm"
                      data-testid={`custom-number-input-${action}`}
                    />
                    <Button
                      size="sm"
                      onClick={() => addCustomNumber(action)}
                      disabled={saving || !newCustomNumber[action]}
                      data-testid={`add-custom-number-${action}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {customNumbers.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {customNumbers.map(num => (
                        <Badge key={num} variant="secondary" className="gap-1">
                          {num}
                          <button onClick={() => removeCustomNumber(action, num)} className="ml-1 hover:text-red-400">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Email Notifications */}
        <div className="space-y-3 pl-2 border-l-2 border-purple-500/30 ml-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-purple-400" />
              <Label className="text-sm font-medium">Email Notifications</Label>
            </div>
            <Switch
              checked={emailEnabled}
              onCheckedChange={() => handleToggle(`job_${action}_email_enabled`)}
              disabled={saving}
              data-testid={`email-${action}-toggle`}
            />
          </div>

          {emailEnabled && (
            <div className="space-y-3 pl-4">
              <p className="text-xs text-muted-foreground">Who should receive email alerts?</p>
              <div className="grid gap-2">
                {RECIPIENT_OPTIONS.filter(opt => opt.id !== 'business_phone' && opt.id !== 'custom').map(option => (
                  <div key={option.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`email-${action}-${option.id}`}
                      checked={emailRecipients.includes(option.id)}
                      onCheckedChange={() => handleRecipientToggle('email', action, option.id)}
                      disabled={saving}
                      data-testid={`email-${action}-${option.id}`}
                    />
                    <label
                      htmlFor={`email-${action}-${option.id}`}
                      className="text-sm cursor-pointer flex items-center gap-2"
                    >
                      <option.icon className="h-3 w-3 text-muted-foreground" />
                      {option.label}
                    </label>
                  </div>
                ))}
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`email-${action}-custom`}
                    checked={emailRecipients.includes('custom')}
                    onCheckedChange={() => handleRecipientToggle('email', action, 'custom')}
                    disabled={saving}
                    data-testid={`email-${action}-custom`}
                  />
                  <label htmlFor={`email-${action}-custom`} className="text-sm cursor-pointer">
                    Custom email addresses
                  </label>
                </div>
              </div>

              {emailRecipients.includes('custom') && (
                <div className="space-y-2 pt-2">
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="email@example.com"
                      value={newCustomEmail[action]}
                      onChange={(e) => setNewCustomEmail({ ...newCustomEmail, [action]: e.target.value })}
                      className="flex-1 h-8 text-sm"
                      data-testid={`custom-email-input-${action}`}
                    />
                    <Button
                      size="sm"
                      onClick={() => addCustomEmail(action)}
                      disabled={saving || !newCustomEmail[action]}
                      data-testid={`add-custom-email-${action}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  {customEmails.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {customEmails.map(email => (
                        <Badge key={email} variant="secondary" className="gap-1">
                          {email}
                          <button onClick={() => removeCustomEmail(action, email)} className="ml-1 hover:text-red-400">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card data-testid="job-notification-settings-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-base sm:text-lg">Job Response Notifications</CardTitle>
        </div>
        <CardDescription>
          Configure who receives SMS and email alerts when technicians accept, reject, or change the status of jobs
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {renderNotificationSection(
          'accepted',
          CheckCircle,
          'When Job is Accepted',
          'text-green-500'
        )}
        {renderNotificationSection(
          'rejected',
          XCircle,
          'When Job is Rejected',
          'text-red-500'
        )}
        {renderNotificationSection(
          'status_changed',
          Tag,
          'When Tech Changes Job Status (Estimate / Deposit Collected / Cancelled)',
          'text-amber-500'
        )}

        {/* Resolved Recipients Diagnostic */}
        {resolvedRecipients && (
          <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg space-y-3" data-testid="resolved-recipients-panel">
            <h4 className="text-blue-400 font-medium text-sm flex items-center gap-2">
              <Users className="w-4 h-4" /> Resolved Recipients Preview
            </h4>
            <p className="text-muted-foreground text-xs">These are the actual phone numbers and emails that will receive notifications:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {/* Owner */}
              <div className="space-y-1">
                <p className="text-foreground font-medium">Owner</p>
                <p className="text-muted-foreground">
                  SMS: {resolvedRecipients.owner?.phone ? (
                    <span className="text-green-400">{resolvedRecipients.owner.phone}</span>
                  ) : (
                    <span className="text-red-400">No valid phone {resolvedRecipients.owner?.phone_raw ? `(stored: ${resolvedRecipients.owner.phone_raw})` : ''}</span>
                  )}
                </p>
                <p className="text-muted-foreground">
                  Email: {resolvedRecipients.owner?.email ? (
                    <span className="text-green-400">{resolvedRecipients.owner.email}</span>
                  ) : <span className="text-red-400">Not set</span>}
                </p>
              </div>
              {/* Business Phone */}
              <div className="space-y-1">
                <p className="text-foreground font-medium">Business Phone</p>
                <p className="text-muted-foreground">
                  {resolvedRecipients.business_phone ? (
                    <span className="text-green-400">{resolvedRecipients.business_phone}</span>
                  ) : <span className="text-red-400">Not set</span>}
                </p>
              </div>
              {/* Admins */}
              <div className="space-y-1">
                <p className="text-foreground font-medium">Admins ({resolvedRecipients.admins?.length || 0})</p>
                {(resolvedRecipients.admins || []).length === 0 ? (
                  <p className="text-muted-foreground text-red-400">No admins found</p>
                ) : resolvedRecipients.admins.map((a, i) => (
                  <p key={i} className="text-muted-foreground">
                    {a.name}: {a.phone ? <span className="text-green-400">{a.phone}</span> : <span className="text-red-400">No valid phone</span>}
                    {a.email ? <span className="ml-1 text-green-400">({a.email})</span> : null}
                  </p>
                ))}
              </div>
              {/* Dispatchers */}
              <div className="space-y-1">
                <p className="text-foreground font-medium">Dispatchers ({resolvedRecipients.dispatchers?.length || 0})</p>
                {(resolvedRecipients.dispatchers || []).length === 0 ? (
                  <p className="text-muted-foreground text-red-400">No dispatchers found</p>
                ) : resolvedRecipients.dispatchers.map((d, i) => (
                  <p key={i} className="text-muted-foreground">
                    {d.name}: {d.phone ? <span className="text-green-400">{d.phone}</span> : <span className="text-red-400">No valid phone</span>}
                    {d.email ? <span className="ml-1 text-green-400">({d.email})</span> : null}
                  </p>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
